import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import styles from './ActivationCockpit.module.css';

/**
 * ActivationCockpit — checklist d'activation affichée en tête du dashboard
 * tant que le restaurant n'a pas son premier scan QR (reco #1 audit 07/07/2026).
 *
 * Remplace les blocs "faux zéros" (Satisfaction 0 %, Impact +0 €, Objectif 0/15)
 * par un moteur d'activation : 5 étapes vers le premier scan. Intègre le rappel
 * de l'objectif pilote (pas de bloc ObjectifPilote doublonné en mode cockpit).
 *
 * Props :
 *  - activation : { activated, progress, total, steps } (backend /api/dashboard)
 *  - slug       : slug du restaurant (CTA "tester le funnel" → /r/:slug)
 *  - goal       : objectif pilote (15) — rappel intégré
 *  - onToggle   : (key, done) => Promise — PATCH d'une étape manuelle
 *  - togglingKey: clé en cours de PATCH (désactive sa checkbox)
 */
export default function ActivationCockpit({
  activation,
  slug,
  goal = 15,
  onToggle,
  togglingKey = null,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');

  if (!activation) return null;
  const { progress, total, steps } = activation;
  const pct = total > 0 ? Math.max(0, Math.min(100, (progress / total) * 100)) : 0;

  const handleCta = (step) => {
    if (step.cta === 'public_funnel') {
      if (slug) window.open(`/r/${slug}`, '_blank', 'noopener');
      return;
    }
    if (step.cta) navigate(step.cta);
  };

  return (
    <Card span={12} id="activation-cockpit">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>
            <span className={styles.icon}>🚀</span> {t('activation.title')}
          </h2>
          <div className={styles.sub}>{t('activation.subtitle')}</div>
        </div>
        <div className={styles.counter} id="activation-progress">
          {progress}/{total}
        </div>
      </div>

      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${pct}%` }} />
      </div>

      <ul className={styles.steps}>
        {steps.map((step) => (
          <li key={step.key} className={`${styles.step} ${step.done ? styles.stepDone : ''}`}>
            {step.auto ? (
              <span
                className={`${styles.check} ${step.done ? styles.checkDone : ''} ${styles.checkAuto}`}
                aria-hidden="true"
              >
                {step.done ? '✓' : ''}
              </span>
            ) : (
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={step.done}
                disabled={togglingKey === step.key}
                onChange={(e) => onToggle && onToggle(step.key, e.target.checked)}
                aria-label={t(`activation.steps.${step.key}`)}
              />
            )}
            <span className={styles.stepLabel}>{t(`activation.steps.${step.key}`)}</span>
            {step.cta && !step.done && (
              <button
                type="button"
                className={styles.stepCta}
                onClick={() => handleCta(step)}
              >
                {t(`activation.ctas.${step.key}`)}
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.mainCta}
          onClick={() => navigate('/qr')}
        >
          {t('activation.main_cta')}
        </button>
        <div className={styles.objectifNote}>
          🎯 {t('activation.objectif_note', { goal })}
        </div>
      </div>
    </Card>
  );
}
